import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { IClientInfo } from '../../lib/types';
import { getThemePreferenceCookie, setThemePreferenceCookie } from '../../lib/theme-cookies';
import { getCachedTheme, setCachedTheme, refreshThemeCache } from '../../lib/theme-cache';
import { useThemeCacheManager, warmThemeCache } from '../../lib/theme-cache-manager';
import { fetchThemeFromAPI } from '../../lib/theme-utils';
import { resolveAssetUrl } from '../../lib/ultra-meet-config';

interface ThemeContextValue {
    theme: IClientInfo;
    currentThemeName: string | null;
    error: string | null;
    switchAllowed: boolean;
    availableThemes: string[];
    isSwitching: boolean;
    isLoading: boolean;
    refreshTheme: () => void;
    switchTheme: (themeName: string) => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: ReactNode;
    initialTheme: IClientInfo;
    initialThemeName?: string | null;
    forceThemeName?: string | null;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme, initialThemeName, forceThemeName }) => {
    const [theme, setTheme] = useState<IClientInfo>(initialTheme);
    const [currentThemeName, setCurrentThemeName] = useState<string | null>(initialThemeName || null);
    const [error, setError] = useState<string | null>(null);
    const [switchAllowed, setSwitchAllowed] = useState(false);
    const [availableThemes, setAvailableThemes] = useState<string[]>([]);
    const [isSwitching, setIsSwitching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [themeCache, setThemeCache] = useState<Map<string, IClientInfo>>(new Map());

    const loadThemeFromCache = (themeName?: string): boolean => {
        const cacheKey = themeName || 'default';

        // Try localStorage cache first
        const localCacheEntry = getCachedTheme(themeName);
        if (localCacheEntry) {
            setTheme(localCacheEntry.themeData);
            setCurrentThemeName(localCacheEntry.themeName);
            setSwitchAllowed(localCacheEntry.switchAllowed);
            setAvailableThemes(localCacheEntry.availableThemes);
            injectCSSVariables(localCacheEntry.themeData);
            updateDocumentHead(localCacheEntry.themeData);
            return true;
        }

        // Try memory cache (existing behavior)
        if (themeCache.has(cacheKey)) {
            const cachedTheme = themeCache.get(cacheKey)!;
            setTheme(cachedTheme);
            setCurrentThemeName(themeName || 'default');
            injectCSSVariables(cachedTheme);
            updateDocumentHead(cachedTheme);
            return true;
        }

        return false;
    };

    const fetchTheme = async (themeName?: string, isInitialLoad: boolean = false) => {
        try {
            setError(null);
            if (isInitialLoad) setIsLoading(true);

            // Try to load from cache first
            if (loadThemeFromCache(themeName)) {
                if (isInitialLoad) setIsLoading(false);

                // Start background refresh if cache is getting old
                setTimeout(() => {
                    refreshThemeCache(themeName);
                }, 100);
                return;
            }

            // If no cache, fetch from API
            const data = await fetchThemeFromAPI(themeName);

            if (data && data.result === 'success' && data.theme) {
                // Cache the theme in all locations
                const cacheKey = themeName || 'default';
                themeCache.set(cacheKey, data.theme);
                setThemeCache(new Map(themeCache));

                // Save to localStorage
                setCachedTheme(themeName || 'default', data.theme, data.switchAllowed || false, data.availableThemes || []);

                // Only set theme if we don't have an initial theme or if this is a theme switch
                if (!initialTheme || !isInitialLoad || themeName) {
                    setTheme(data.theme);
                    injectCSSVariables(data.theme);
                    updateDocumentHead(data.theme);
                }

                // Track the current theme name
                setCurrentThemeName(themeName || 'default');

                setSwitchAllowed(data.switchAllowed || false);
                setAvailableThemes(data.availableThemes || []);

                // Theme loaded successfully
                if (isInitialLoad) setIsLoading(false);
            } else {
                throw new Error('Invalid theme response');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            console.error('Theme fetch error:', err);

            // Don't use fallback theme - let components handle loading state
            // This prevents theme blinking by not setting any theme data until the real theme loads
            if (isInitialLoad) setIsLoading(false);
        }
    };

    const injectCSSVariables = (themeData: IClientInfo) => {
        const styleId = 'theme-css-variables';
        let styleElement = document.getElementById(styleId) as HTMLStyleElement;

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        // Theme-switch transitions are scoped under [data-lk-theme] (the component
        // root) so they never leak into a host app when embedded. The :root CSS
        // variables stay global on purpose, since components read them via var().
        const cssVariables = `
            :root {
                --brand-color: ${themeData.colors.brand};
                --background-color: ${themeData.colors.background};
                --surface-color: ${themeData.colors.surface};
                --placeholder-color: ${themeData.colors.placeholder};
            }

            /* Smooth transitions for theme switching */
            [data-lk-theme] * {
                transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
            }

            /* Smooth transitions for images */
            [data-lk-theme] .theme-image {
                transition: opacity 0.3s ease, transform 0.3s ease;
            }

            [data-lk-theme] .theme-image.switching {
                opacity: 0.8;
                transform: scale(0.98);
            }

            /* Smooth transitions for text content */
            [data-lk-theme] :is(h1, h2, h3, h4, h5, h6, p, span) {
                transition: color 0.3s ease, opacity 0.3s ease;
            }

            /* Smooth transitions for buttons */
            [data-lk-theme] button {
                transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease, transform 0.1s ease;
            }

            [data-lk-theme] button:hover {
                transform: translateY(-1px);
            }

            [data-lk-theme] button:active {
                transform: translateY(0);
            }
        `;

        styleElement.innerHTML = cssVariables;
    };

    const updateDocumentHead = (themeData: IClientInfo) => {
        // Update title
        if (themeData.title) {
            document.title = themeData.title;
        }

        // Update favicon
        updateOrCreateMetaTag('link', { rel: 'icon', href: resolveAssetUrl(themeData.favicon) });

        // Update apple-touch-icon
        updateOrCreateMetaTag('link', { rel: 'apple-touch-icon', href: resolveAssetUrl(themeData.appleTouch) });

        // Update safari-pinned-tab
        updateOrCreateMetaTag('link', { rel: 'mask-icon', href: resolveAssetUrl(themeData.safariPinnedTab) });

        // Update Open Graph image
        updateOrCreateMetaTag('meta', { property: 'og:image', content: resolveAssetUrl(themeData.openGraph) });

        // Update Open Graph title
        updateOrCreateMetaTag('meta', { property: 'og:title', content: themeData.title });

        // Update description with slogan
        updateOrCreateMetaTag('meta', { name: 'description', content: themeData.slogan });
        updateOrCreateMetaTag('meta', { property: 'og:description', content: themeData.slogan });
    };

    const updateOrCreateMetaTag = (tagName: string, attributes: Record<string, string>) => {
        const selector =
            tagName === 'meta'
                ? `meta[${Object.keys(attributes)[0]}="${Object.values(attributes)[0]}"]`
                : `link[${Object.keys(attributes)[0]}="${Object.values(attributes)[0]}"]`;

        let element = document.querySelector(selector);

        if (!element) {
            element = document.createElement(tagName);
            document.head.appendChild(element);
        }

        Object.entries(attributes).forEach(([key, value]) => {
            element!.setAttribute(key, value);
        });
    };

    const refreshTheme = () => {
        fetchTheme(undefined, true);
    };

    const switchTheme = async (themeName: string) => {
        if (!switchAllowed) {
            throw new Error('Theme switching is not allowed');
        }

        setIsSwitching(true);

        try {
            // Add switching class to images for smooth transition
            const images = document.querySelectorAll('.theme-image');
            images.forEach((img) => img.classList.add('switching'));

            // Try to load from cache first for instant switching
            if (loadThemeFromCache(themeName)) {
                // Theme loaded from cache instantly
                setThemePreferenceCookie(themeName);

                // Start background refresh to keep cache fresh
                setTimeout(() => {
                    refreshThemeCache(themeName);
                }, 100);
            } else {
                // No cache available, fetch from API
                await fetchTheme(themeName);
                setThemePreferenceCookie(themeName);
            }

            // Remove switching class after a short delay
            setTimeout(() => {
                images.forEach((img) => img.classList.remove('switching'));
            }, 300);
        } finally {
            setIsSwitching(false);
        }
    };

    // Preload available themes for faster switching
    const preloadThemes = async () => {
        if (!availableThemes || availableThemes.length === 0) return;

        const preloadPromises = availableThemes.map(async (themeName) => {
            if (!themeCache.has(themeName)) {
                try {
                    const apiResponse = await fetchThemeFromAPI(themeName);
                    if (apiResponse && apiResponse.result === 'success' && apiResponse.theme) {
                        themeCache.set(themeName, apiResponse.theme);
                    }
                } catch (error) {
                    console.warn(`Failed to preload theme ${themeName}:`, error);
                }
            }
        });

        await Promise.all(preloadPromises);
        setThemeCache(new Map(themeCache));
    };

    useEffect(() => {
        // Inject initial theme CSS variables immediately and mark as loaded
        injectCSSVariables(initialTheme);
        updateDocumentHead(initialTheme);
        setIsLoading(false);

        // Check for cookie preference for potential theme switching, unless the host app locks the theme.
        const cookieTheme = forceThemeName ? null : getThemePreferenceCookie();
        let preferredTheme: string | undefined;
        if (cookieTheme) {
            preferredTheme = cookieTheme;
            setCurrentThemeName(cookieTheme);
        }

        // Try to load from cache first, then fallback to API. When the host app
        // forces a theme, trust the provided initialTheme and avoid cross-origin
        // theme requests.
        const themeName = forceThemeName || preferredTheme || initialThemeName || undefined;
        if (forceThemeName) {
            setCurrentThemeName(forceThemeName);
            setSwitchAllowed(false);
            setAvailableThemes([forceThemeName]);
            setCachedTheme(forceThemeName, initialTheme, false, [forceThemeName]);
            setIsLoading(false);
            return;
        }

        if (loadThemeFromCache(themeName)) {
            // Theme loaded from cache, no need for API call
            setIsLoading(false);

            // Start background refresh to keep cache fresh
            setTimeout(() => {
                refreshThemeCache(themeName);
            }, 1000);
        } else {
            // No cache available, fetch from API
            fetchTheme(themeName, true);
        }
    }, [initialTheme, initialThemeName, forceThemeName]);

    // Preload themes when availableThemes is populated
    useEffect(() => {
        if (availableThemes.length > 0) {
            preloadThemes();
        }
    }, [availableThemes]);

    // Use cache manager for lifecycle management
    useThemeCacheManager(currentThemeName);

    // Warm cache when available themes are loaded
    useEffect(() => {
        if (availableThemes.length > 0) {
            warmThemeCache(availableThemes);
        }
    }, [availableThemes]);

    const value: ThemeContextValue = {
        theme,
        currentThemeName,
        error,
        switchAllowed,
        availableThemes,
        isSwitching,
        isLoading,
        refreshTheme,
        switchTheme
    };

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
